import { useState, useEffect, useId, useRef, ReactNode, FocusEvent, KeyboardEvent } from "react";
import TextField from "@/components/TextField";
import type { TextFieldType } from "@material/web/textfield/internal/text-field";
import { LitMenu, LitMenuItem, MdMenu } from "@/components/Menu/LitMenu";
import { MenuDivider } from "@/components/Menu/menuStyles";
import Progress from "../Progress";
import { SlotIconName } from "../Icon/SlotIcon";
import Icon from "../Icon";
import classNames from "classnames";
import { usePlatformDetection } from "@/hooks/usePlatformDetection";
import { syncAutocompleteFieldA11y } from "./syncAutocompleteFieldA11y";

type AutocompleteProps<T> = {
  autofocus?: boolean;
  renderItem: (option: T) => JSX.Element;
  shouldDivideAfterItem?: (option: T) => boolean;
  label: string;
  data: T[];
  query?: string;
  onChangeText: (value: string) => void;
  reset?(): void;
  onSelect: (value: T) => void;
  onBlur?: (e: FocusEvent<HTMLElement>) => void;
  onMouseDown?: (e: MouseEvent) => void;
  error?: boolean;
  errorText?: string;
  required?: boolean;
  disabled?: boolean;
  variant?: "searchableDropdown" | "default";
  leadingIcon?: SlotIconName | null;
  menuClassName?: string;
  loadingResults?: boolean;
  placeholder?: string;
  name?: string;
  type?: TextFieldType;
  supportingText?: string;
  constrainMenuWidth?: boolean;
  menuPositioning?: "absolute" | "fixed" | "popover";
  "data-testid"?: string;
};

export type MenuItem = {
  onClick?(): void;
  onMouseDown?: (e: any) => void;
  disabled?: boolean;
  children: ReactNode;
  useDivider?: boolean;
};

export default function Autocomplete<T>({
  autofocus,
  renderItem,
  shouldDivideAfterItem,
  label,
  data,
  query,
  onChangeText,
  reset,
  onSelect,
  onBlur,
  onMouseDown,
  error,
  errorText,
  required,
  disabled,
  variant,
  leadingIcon = "search",
  menuClassName = "max-h-[175px]",
  loadingResults = false,
  placeholder,
  name,
  type,
  supportingText,
  constrainMenuWidth = false,
  menuPositioning = "popover",
  "data-testid": dataTestId
}: AutocompleteProps<T> & { name?: string }) {
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<MdMenu>(null);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { isMobile } = usePlatformDetection();
  const descriptionId = useId();
  const descriptionText = error && errorText ? errorText : supportingText && !error ? supportingText : "";

  const anchorElement = anchorRef.current;
  // TextField's ref is typed as HTMLInputElement, but production uses a Material
  // custom element host; Vitest's LitTextField mock puts the ref on an <input>.
  const textFieldHost = textFieldRef.current as HTMLElement | null;
  const textFieldElement = (textFieldHost?.shadowRoot?.querySelector("input") ??
    (textFieldHost instanceof HTMLInputElement ? textFieldHost : null)) as HTMLInputElement | null;
  const menuElement = menuRef.current?.shadowRoot?.querySelector(".menu") as MdMenu | undefined;

  useEffect(() => {
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    if (autofocus && textFieldRef.current) {
      timeouts.push(
        setTimeout(() => {
          textFieldRef.current?.focus();
        }, 100)
      );
      timeouts.push(
        setTimeout(() => {
          setVisibility(true);
        }, 300)
      );
    }
    return () => {
      timeouts.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autofocus, textFieldRef.current]);

  useEffect(() => {
    let clickOutsideTimeout: ReturnType<typeof setTimeout> | undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (clickOutsideTimeout) clearTimeout(clickOutsideTimeout);
      clickOutsideTimeout = setTimeout(() => {
        const menuElement = menuRef.current?.shadowRoot?.querySelector(".menu");
        const target = event.target as Node;

        const isMenuItem =
          typeof Element !== "undefined" && target instanceof Element && target.closest("md-menu-item");

        // Don't close if clicking on a menu item
        if (isMenuItem) {
          return;
        }

        const isInsideAnchor = anchorRef.current?.contains(target);
        const isInsideMenu = menuRef.current?.contains(target) || menuElement?.contains(target);

        // Close dropdown if clicking outside the input anchor and menu surface.
        if (!isInsideAnchor && !isInsideMenu) {
          setVisibility(false);
        }
      }, 100);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (clickOutsideTimeout) clearTimeout(clickOutsideTimeout);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (menuElement) {
      const anchorElementWidth = anchorElement?.getBoundingClientRect().width;
      if (anchorElementWidth) {
        menuElement.style.minWidth = `${anchorElementWidth}px`;
        if (constrainMenuWidth) {
          menuElement.style.maxWidth = `${anchorElementWidth}px`;
          menuElement.style.width = `${anchorElementWidth}px`;
        }
      }
    }
    // Note: constrainMenuWidth is intentionally NOT in the dependency array
    // to avoid re-running the effect on every render, which causes focus loss
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuElement, anchorElement, textFieldElement]);

  useEffect(() => {
    if (menuElement) {
      menuElement.style.height = "auto";
    }
  }, [menuElement, query]);

  useEffect(() => {
    const host = textFieldRef.current as HTMLElement | null;
    const syncFieldA11y = () => syncAutocompleteFieldA11y(host, descriptionText, descriptionId);

    syncFieldA11y();
    const raf = requestAnimationFrame(syncFieldA11y);

    // Material rewrites slotted #description when value length (`count`) changes.
    // Re-apply after each input so helper/error announcement text is not wiped.
    const shadowInput = host?.shadowRoot?.querySelector("input");
    shadowInput?.addEventListener("input", syncFieldA11y);

    return () => {
      cancelAnimationFrame(raf);
      shadowInput?.removeEventListener("input", syncFieldA11y);
    };
  }, [descriptionText, descriptionId, query, textFieldElement]);

  const items = data.map((option) => ({
    children: renderItem(option),
    useDivider: shouldDivideAfterItem?.(option) ?? false,
    onClick: () => {
      onSelect(option);
      // Blur input on mobile to close keyboard after selection
      if (isMobile && textFieldElement) {
        textFieldElement.blur();
      }
    },
    onMouseDown: (e: MouseEvent) => onMouseDown?.(e)
  }));

  const menuItems =
    items.length > 0
      ? items
      : [
          {
            children: loadingResults ? (
              <div className="mx-auto">
                <Progress />
              </div>
            ) : (
              <>No results found</>
            ),
            disabled: true
          }
        ];

  const setVisibility = (isOpen: boolean) => {
    setIsMenuOpen(isOpen);
    if (isOpen) {
      menuRef.current?.show();
    } else {
      menuRef.current?.close();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      setHighlightedIndex((prevIndex) =>
        prevIndex === null || prevIndex + 1 === menuItems.length - 1 ? 0 : prevIndex + 1
      );
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setHighlightedIndex((prevIndex) =>
        prevIndex === null || prevIndex === 0 ? menuItems.length - 1 : prevIndex - 1
      );
      e.preventDefault();
    } else if (e.key === "Enter" && highlightedIndex !== null) {
      const selectedItem = menuItems[highlightedIndex];
      if ("onClick" in selectedItem && typeof selectedItem.onClick === "function") {
        selectedItem.onClick();
        setHighlightedIndex(null);
        setVisibility(false);
        // Blur input on mobile to close keyboard after Enter key selection
        if (isMobile && textFieldElement) {
          textFieldElement.blur();
        }
      }
      e.preventDefault();
    } else if (e.key === "Escape") {
      setVisibility(false);
    }
  };

  const trailingIcon =
    variant === "searchableDropdown" && !query ? (
      "arrow_drop_down"
    ) : query && reset ? (
      <button
        aria-label="Clear autocomplete"
        className={classNames("flex", { "opacity-50 cursor-not-allowed": disabled, "cursor-pointer": !disabled })}
        onClick={() => {
          if (!disabled) {
            reset();
            textFieldElement?.focus();
          }
        }}
      >
        <Icon name="cancel" />
      </button>
    ) : undefined;

  const handleFocus = () => {
    if (variant === "searchableDropdown") {
      setVisibility(true);
    }
  };

  const handleBlur = (e: FocusEvent<HTMLElement>) => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    blurTimeoutRef.current = setTimeout(() => {
      if (!isMenuOpen) {
        onBlur?.(e);
      }
    }, 200);
  };

  return (
    <div className="relative" data-testid={dataTestId}>
      <div
        className="relative min-w-full"
        onClick={() => {
          if (variant === "searchableDropdown") {
            setVisibility(true);
            textFieldElement?.focus();
          }
        }}
        onKeyDown={handleKeyDown}
        ref={anchorRef}
      >
        <TextField
          disabled={disabled || false}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          value={query}
          onChangeText={(e) => {
            onChangeText(e);
            setVisibility(true);
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className="w-full textfield"
          leadingIcon={leadingIcon ? leadingIcon : undefined}
          trailingIcon={trailingIcon}
          error={error}
          required={required}
          ref={textFieldRef}
          name={name}
          type={type}
        />
      </div>
      {error && errorText && (
        <p id={descriptionId} className="mt-1 ml-3 text-body-small text-sys-brand-error" role="alert">
          {errorText}
        </p>
      )}
      {supportingText && !error && (
        <p id={descriptionId} className="mt-1 ml-3 text-body-small text-sys-brand-on-surface-variant">
          {supportingText}
        </p>
      )}
      <LitMenu
        anchorElement={anchorRef.current}
        ref={menuRef}
        positioning={menuPositioning}
        quick
        className={classNames(`${menuClassName} overflow-y-auto z-50`, {
          hidden: !textFieldRef.current?.value && menuItems.filter((item) => !("disabled" in item)).length === 0
        })}
      >
        {menuItems.map(({ onClick, onMouseDown, disabled, children, useDivider }: MenuItem, index) => (
          <div key={`menu-item-${index}`} className="w-full">
            <LitMenuItem
              className={`w-full ${highlightedIndex === index ? "bg-sys-brand-surface-dim" : ""}`}
              onClick={() => {
                onClick?.();
                setVisibility(false);
              }}
              onMouseDown={onMouseDown}
              disabled={disabled}
            >
              <div className="flex flex-row items-center">{children}</div>
            </LitMenuItem>
            {useDivider && <MenuDivider inset={false} className="mb-2" />}
          </div>
        ))}
      </LitMenu>
    </div>
  );
}
